import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import CreditIcon from '../CreditIcon'
import PriceChart from '../wrestler/PriceChart'
import ScoreChart from '../wrestler/ScoreChart'
import { PREMIUM_ICON_CLASS } from '../../utils/premiumAccess'
import { getBrandBadgeClass } from '../../utils/brandBadge'

const toDate = value => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value?.toDate === 'function') return value.toDate()
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000)

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatDate = value => {
  const date = toDate(value)
  return date ? date.toLocaleDateString('it-IT') : 'N/D'
}

const startOfDay = date => {
  const day = new Date(date)
  day.setHours(0, 0, 0, 0)
  return day
}

const formatDaysSinceDate = value => {
  const date = toDate(value)
  if (!date) return ''

  const days = Math.floor(
    (startOfDay(new Date()).getTime() - startOfDay(date).getTime()) /
    (24 * 60 * 60 * 1000)
  )

  if (days < 0) {
    const futureDays = Math.abs(days)
    return `tra ${futureDays} ${futureDays === 1 ? 'gg' : 'gg'}`
  }

  return `${days} ${days === 1 ? 'gg' : 'gg'} fa`
}

const formatLabelWithDaysSince = (label, value) => {
  const daysSince = formatDaysSinceDate(value)
  return daysSince ? `${label} - ${daysSince}` : label
}

const formatSigned = value => {
  const number = Number(value)
  if (!Number.isFinite(number)) return '0'
  return `${number > 0 ? '+' : ''}${number}`
}

const formatLineupAdvicePoints = value => {
  const number = Number(value)
  return `${Number.isFinite(number) ? number : 0} pt ultime 3`
}

const formatLineupAdviceSummary = advice => {
  if (!advice) return formatLineupAdvicePoints(0)

  const details = [formatLineupAdvicePoints(advice.recentPoints)]

  if (advice.isInactive) {
    details.push('non attivo')
  }

  if (advice.isInjured) {
    details.push('infortunato')
  }

  return details.join(' - ')
}

const getPriceEntryValue = entry => {
  const number = Number(entry?.value ?? entry?.price ?? entry)
  return Number.isFinite(number) ? number : 0
}

const getPriceDelta = prices => {
  if (!Array.isArray(prices) || prices.length < 2) return null

  const current = getPriceEntryValue(prices[prices.length - 1])
  const previous = getPriceEntryValue(prices[prices.length - 2])
  const delta = current - previous
  const percentage = previous
    ? ((delta / previous) * 100).toFixed(1)
    : '0.0'

  return {
    delta,
    percentage,
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'same'
  }
}

const getLineupAdviceLabel = advice => {
  if (!advice) return ''
  if (advice.primary === 'main-eventer') return 'Main Eventer'
  if (advice.primary === 'backstage') return 'Backstage'
  return 'In-Ring'
}

const getPremiumPositionLabel = value => {
  const position = Number(value) || 0
  if (position >= 5) return 'Main Eventer'
  if (position === 4) return 'Upper'
  if (position === 3) return 'Mid'
  if (position === 2) return 'Low'
  if (position === 1) return 'Jobber'
  return 'Non TV'
}

const getPremiumPositionClassName = value => {
  const position = Number(value) || 0

  if (position >= 5) return 'text-wrestlers-division-5'
  if (position === 4) return 'text-wrestlers-division-4'
  if (position === 3) return 'text-wrestlers-division-3'
  if (position === 2) return 'text-wrestlers-division-2'
  if (position === 1) return 'text-wrestlers-division-1'
  return 'text-wrestlers-division-0'
}

const POSITION_LEGEND = [
  {
    label: 'Non TV',
    description: 'Non appare da tempo per ragioni fisiche o organizzative'
  },
  {
    label: 'Jobber',
    description: 'Poco rilievo e poca presenza, serve a valorizzare altri'
  },
  {
    label: 'Low-card',
    description: 'Parte inferiore della card, ruolo marginale'
  },
  {
    label: 'Mid-card',
    description: 'Parte centrale, ruolo importante ma non principale'
  },
  {
    label: 'Upper',
    description: 'Molto esposto, presente e figura centrale della WWE'
  },
  {
    label: 'Main Eventer',
    description:
      'Chiude spesso gli show, ha match di cartello e rappresenta il brand'
  }
]

function PremiumLegendLabel({ label, items = [], ariaLabel }) {
  const [open, setOpen] = useState(false)

  if (!items.length) return label

  const toggleLegend = event => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(prev => !prev)
  }

  return (
    <span className='premium-info-label'>
      <span className='premium-info-label__text'>{label}</span>
      <button
        type='button'
        className='premium-legend-button'
        onClick={toggleLegend}
        aria-label={ariaLabel || `Legenda ${label}`}
        title={ariaLabel || `Legenda ${label}`}
        aria-expanded={open}
      >
        <i className='bi bi-info-circle' aria-hidden='true'></i>
      </button>
      {open && (
        <span className='premium-legend-popover'>
          {items.map(item => (
            <span className='premium-legend-popover__row' key={item.label}>
              <strong>{item.label}</strong>
              <span>{item.description}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  )
}

export function PremiumFeaturePill({ label = 'Premium', className = '' }) {
  return (
    <span className={`premium-feature-pill ${className}`.trim()}>
      <i className={`bi ${PREMIUM_ICON_CLASS}`} aria-hidden='true'></i>
      {label}
    </span>
  )
}

export function PremiumInfoButton({
  title,
  children,
  className = '',
  buttonClassName = '',
  ariaLabel = 'Info premium',
  dialogClassName = '',
  bodyClassName = '',
  iconClassName = PREMIUM_ICON_CLASS
}) {
  const [open, setOpen] = useState(false)

  const handleOpen = event => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(true)
  }

  const handleClose = event => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    setOpen(false)
  }

  const dialog =
    open && typeof document !== 'undefined'
      ? ReactDOM.createPortal(
        <div className='premium-info-overlay' onClick={handleClose}>
          <div
            className={`premium-info-dialog ${dialogClassName}`.trim()}
            role='dialog'
            aria-modal='true'
            onClick={event => event.stopPropagation()}
          >
            <div className='premium-info-dialog__header'>
              <div className='premium-info-dialog__title'>
                <i className={`bi ${iconClassName}`} aria-hidden='true'></i>
                <span>{title}</span>
              </div>
              <button
                type='button'
                className='premium-info-dialog__close'
                onClick={handleClose}
                aria-label='Chiudi'
              >
                <i className='bi bi-x-lg'></i>
              </button>
            </div>
            <div className={`premium-info-dialog__body ${bodyClassName}`.trim()}>
              {children}
            </div>
          </div>
        </div>,
        document.body
      )
      : null

  return (
    <>
      <button
        type='button'
        className={`premium-info-button ${buttonClassName}`.trim()}
        onClick={handleOpen}
        title={ariaLabel}
        aria-label={ariaLabel}
      >
        <i className={`bi ${iconClassName}`} aria-hidden='true'></i>
      </button>
      <span className={className}>{dialog}</span>
    </>
  )
}

export function PremiumWrestlerCommentButton({ wrestler, comment }) {
  const text = String(comment || wrestler?.premiumComment || '').trim()
  if (!text) return null

  return (
    <PremiumInfoButton title={`${wrestler.name}`} ariaLabel='Commento Premium'>
      <div className='premium-comment-callout'>
        <small>Team FantaWrestling</small>
        <p>{text}</p>
      </div>
    </PremiumInfoButton>
  )
}

export function PremiumWrestlerInsightButton({
  wrestler,
  insight = null,
  isLoading = false,
  buttonClassName = '',
  lineupAdvice = null
}) {
  if (!wrestler) return null

  const comment = String(insight?.teamComment || wrestler.premiumComment || '').trim()
  const titles = Array.isArray(wrestler.championTypes)
    ? wrestler.championTypes.filter(Boolean)
    : []
  const priceHistory = Array.isArray(wrestler.prices) ? wrestler.prices : []
  const currentPrice = insight?.currentPrice ?? wrestler.price ?? 0
  const priceDelta = getPriceDelta(priceHistory)
  const wrestlerScore = Number(wrestler.score) || 0
  const brandLabel = wrestler.brand || 'N/D'
  const record = insight?.record || { wins: 0, losses: 0, draws: 0 }
  const lastThreeShowPoints = Number(insight?.lastThreeShowPoints) || 0
  const lastThreeShows = Array.isArray(insight?.lastThreeShows)
    ? insight.lastThreeShows.filter(showEntry => Number(showEntry?.points) > 0)
    : []
  const announcedShows = Array.isArray(insight?.announcedShows)
    ? insight.announcedShows
    : []
  const showAnnouncements = lastThreeShowPoints === 0 && announcedShows.length > 0
  const detailShows = showAnnouncements ? announcedShows : lastThreeShows
  const detailTitle = showAnnouncements
    ? 'Show annunciati'
    : 'Dettaglio ultime 3 partecipazioni'
  const scoreChartData = lastThreeShows
    .map(showEntry => ({
      date: toDate(showEntry.date),
      total: Number(showEntry.points) || 0
    }))
    .filter(showEntry => showEntry.date instanceof Date)
  const positionLabel = getPremiumPositionLabel(wrestler.position)
  const positionClassName = getPremiumPositionClassName(wrestler.position)
  const commentText = comment || ''
  const lineupAdviceTitle = lineupAdvice
    ? getLineupAdviceLabel(lineupAdvice)
    : 'Da monitorare'
  const lineupAdviceSummary = lineupAdvice
    ? formatLineupAdviceSummary(lineupAdvice)
    : 'Dati insufficienti per un suggerimento automatico'

  return (
    <PremiumInfoButton
      title={wrestler.name || 'Wrestler'}
      ariaLabel={`Info Premium ${wrestler.name || 'wrestler'}`}
      buttonClassName={buttonClassName}
      dialogClassName='premium-info-dialog--insight'
      bodyClassName='premium-info-dialog__body--insight'
    >
      {isLoading ? (
        <div className='premium-info-empty'>Caricamento dati premium...</div>
      ) : (
        <div className='premium-insight-modal'>
          {titles.map(title => (
            <span
              key={title}
              className='premium-insight-chip premium-insight-chip--champion'
            >
              {title}
            </span>
          ))}
          {commentText && (
            <div className='premium-insight-comment'>
              <div className='premium-insight-section-head'>
                <small>Team FantaWrestling dice...</small>
                <div className='premium-insight-hero__pills'>
                </div>
              </div>
              <p>{commentText}</p>
            </div>
          )}

          <div className='premium-insight-summary-grid'>
            <div
              className={`premium-lineup-advice ${lineupAdvice
                  ? `premium-lineup-advice--${lineupAdvice.primary}`
                  : 'premium-lineup-advice--empty'
                }`.trim()}
            >
              <small>
                <i className='bi bi-stars' aria-hidden='true'></i>
                Suggerimento line-up
              </small>
              <strong>{lineupAdviceTitle}</strong>
              <span>{lineupAdviceSummary}</span>
            </div>

            <div className='premium-insight-highlight'>
              <span className='premium-insight-highlight__label'>
                Quotazione attuale
              </span>
              <strong className='premium-insight-highlight__value text-money'>
                <CreditIcon size={18} />
                {currentPrice}
              </strong>
              <span
                className={`premium-insight-highlight__delta ${priceDelta?.direction === 'up'
                    ? 'is-up'
                    : priceDelta?.direction === 'down'
                      ? 'is-down'
                      : 'is-neutral'
                  }`}
              >
                {priceDelta
                  ? `${formatSigned(priceDelta.delta)} (${priceDelta.percentage}%)`
                  : 'Storico non disponibile'}
              </span>
            </div>
          </div>

          <div className='premium-insight-quick-grid'>
            <div className='premium-insight-quick-card'>
              <small>Quotazione</small>
              <strong className='text-money d-inline-flex align-items-center gap-1'>
                <CreditIcon size={16} />
                {currentPrice}
              </strong>
            </div>

            <div className='premium-insight-quick-card'>
              <small>Score</small>
              <strong>{wrestlerScore}</strong>
            </div>

            <div className='premium-insight-quick-card'>
              <small>Trend quota</small>
              <strong
                className={`premium-insight-quick-card__trend ${priceDelta?.direction === 'up'
                    ? 'is-up'
                    : priceDelta?.direction === 'down'
                      ? 'is-down'
                      : 'is-neutral'
                  }`}
              >
                {priceDelta
                  ? `${formatSigned(priceDelta.delta)} (${priceDelta.percentage}%)`
                  : 'Storico non disponibile'}
              </strong>
            </div>

            <div className='premium-insight-quick-card'>
              <small>Brand</small>
              <strong>
                <span className={getBrandBadgeClass(brandLabel)}>{brandLabel}</span>
              </strong>
            </div>
          </div>

          <div className='premium-insight-metrics'>
            <div className='premium-insight-metric premium-insight-metric--points'>
              <small>Ultime 3 partecipazioni</small>
              <strong
                className={
                  lastThreeShowPoints > 0
                    ? 'premium-info-value--points'
                    : 'premium-info-value--empty'
                }
              >
                {formatSigned(lastThreeShowPoints)}
              </strong>
            </div>

            <div className='premium-insight-metric premium-insight-metric--record'>
              <small>Record</small>
              <strong className='premium-info-value--record'>
                {record.wins || 0}v - {record.losses || 0}s
              </strong>
            </div>

            <div className='premium-insight-metric premium-insight-metric--position'>
              <small>
                <PremiumLegendLabel
                  label='Posizione'
                  items={POSITION_LEGEND}
                  ariaLabel='Legenda posizione'
                />
              </small>
              <strong className={positionClassName}>{positionLabel}</strong>
            </div>
          </div>

          <div className='premium-insight-meta-grid'>
            <div className='premium-insight-meta-card'>
              <small>
                {formatLabelWithDaysSince(
                  'Ultima apparizione',
                  insight?.lastAppearance?.date
                )}
              </small>
              <strong>{formatDate(insight?.lastAppearance?.date)}</strong>
              <span>{insight?.lastAppearance?.showName || 'N/D'}</span>
            </div>

            <div className='premium-insight-meta-card'>
              <small>
                {formatLabelWithDaysSince('Ultimo match', insight?.lastMatch?.date)}
              </small>
              <strong>{formatDate(insight?.lastMatch?.date)}</strong>
              <span>{insight?.lastMatch?.showName || 'N/D'}</span>
            </div>
          </div>

          <div className='premium-insight-charts'>
            <div className='premium-insight-chart-card'>
              <div className='premium-insight-chart-card__head'>
                <small>Trend recente</small>
                <strong>Punti ultimi 3 show</strong>
              </div>
              {scoreChartData.length >= 2 ? (
                <ScoreChart
                  data={scoreChartData}
                  average={lastThreeShowPoints / Math.max(scoreChartData.length, 1)}
                  compact
                  height={160}
                />
              ) : (
                <div className='premium-insight-chart-card__empty'>
                  Trend recente non disponibile
                </div>
              )}
            </div>

            <div className='premium-insight-chart-card'>
              <div className='premium-insight-chart-card__head'>
                <small>Storico</small>
                <strong>Andamento quotazione</strong>
              </div>
              {priceHistory.length >= 2 ? (
                <PriceChart data={priceHistory} compact height={160} />
              ) : (
                <div className='premium-insight-chart-card__empty'>
                  Storico quotazione non disponibile
                </div>
              )}
            </div>
          </div>

          <div className='premium-info-list premium-info-list--insight'>
            <small>{detailTitle}</small>
            {detailShows.length > 0 ? (
              detailShows.map(showEntry => (
                <div
                  key={showEntry.showId || showEntry.showName}
                  className='premium-info-list__row'
                >
                  <span>{formatDate(showEntry.date)} - {showEntry.showName || 'Show'}</span>
                  <strong
                    className={`premium-info-list__value ${showEntry.isAnnouncement
                        ? 'premium-info-list__value--announcement'
                        : 'premium-info-list__value--points'
                      }`.trim()}
                  >
                    {showEntry.isAnnouncement
                      ? 'Annunciato'
                      : formatSigned(showEntry.points || 0)}
                  </strong>
                </div>
              ))
            ) : (
              <div className='premium-info-list__row premium-info-list__row--empty'>
                <span>Nessun dato recente disponibile</span>
                <strong className='premium-info-list__value premium-info-list__value--empty'>
                  N/D
                </strong>
              </div>
            )}
          </div>
        </div>
      )}
    </PremiumInfoButton>
  )
}

export function PremiumDivisionStats({ wrestler }) {
  if (!wrestler) return null

  const position = Number(wrestler.position || 0)

  return (
    <div className='premium-info-grid premium-info-grid--compact'>
      <div className='d-flex gap-1 justify-content-between'>
        <small className='d-flex align-items-center gap-1'>
          <PremiumLegendLabel
            label='Posizione'
            items={POSITION_LEGEND}
            ariaLabel='Legenda posizione'
          />
        </small>
        <strong
          className={`d-flex justify-content-end ${getPremiumPositionClassName(
            position
          )}`.trim()}
        >
          {getPremiumPositionLabel(position)}
        </strong>
      </div>
    </div>
  )
}
